import { LinkedListNode } from "./LinkedList";
// We use a 2d layout array design
// The goal is to put linked list structures in the same row
// Other objects get their own row
// a linked list is defined as a list of objects where each object has a reference to the next object in the list
// and all objects are structurally equivalent
export class HeapLayout {
    constructor(frame, previous) {
        if (frame.event === 'uncaught_exception') {
            return; // don't render anything
        }
        this.heap = frame.heap;
        this.seen = new Set();
        this.layout = [];
        this.nodeById = new Map();
        // start from the previous layout
        // this avoids re-rendering the entire heap on every step
        // and it leads to a more stable visualization
        if (previous) {
            // deep clone the previous layout with references intact
            previous.layout.forEach(node => {
                const arr = node.toArray();
                let prev = arr[0];
                this.insertNode(prev);
                for (let i = 1; i < arr.length; i++) {
                    this.insertAfterParent(arr[i], prev);
                    prev = arr[i];
                }
            });
        }
        // recurse all references from the globals and stack
        // this will add any new nodes to the layout
        frame.ordered_globals.forEach(name => {
            this.recurseSubEl(frame.globals[name]);
        });
        frame.stack_to_render.forEach(stackEl => {
            stackEl.ordered_varnames.forEach(name => {
                this.recurseSubEl(stackEl.encoded_locals[name]);
            });
        });
        // remove any nodes that are no longer referenced from the stack or globals
        this.nodeById.forEach((node, id) => {
            if (!this.seen.has(id)) {
                this.nodeById.delete(id);
                node.remove();
            }
        });
        // remove empty start nodes
        this.layout = this.layout.map(node => {
            if (node.value === undefined) {
                return node.next;
            }
            return node;
        }).filter(node => node !== null);
    }
    insertBeforeChild(childId, parentId) {
        const node = this.nodeById.get(childId).insertBefore(parentId);
        this.nodeById.set(parentId, node);
    }
    insertAfterParent(childId, parentId) {
        const node = this.nodeById.get(parentId).insertAfter(childId);
        this.nodeById.set(childId, node);
    }
    insertNode(id) {
        const node = new LinkedListNode(id);
        this.layout.push(node);
        this.nodeById.set(id, node);
    }
    recurseSubEl(childEl, parentId = undefined) {
        if (isReference(childEl)) {
            const childId = childEl[1];
            const child = this.heap[childId];
            if (parentId && isStructurallyEquivalent(child, this.heap[parentId])) {
                // we have detected a linked list like structure
                // we want to put all the elements in the same row
                // if the parent is in the layout, and child isn't, insert the child after the parent
                if (!this.nodeById.has(childId) && this.nodeById.has(parentId)) {
                    this.insertAfterParent(childId, parentId);
                }
                this.recurseHeapEl(childId);
                // we know that the child is in the layout now
                // if the parent isn't in the layout, insert the parent before the child
                if (!this.nodeById.has(parentId) && this.nodeById.has(childId)) {
                    this.insertBeforeChild(childId, parentId);
                }
            }
            else if (!shouldNest(child) || !parentId) {
                this.recurseHeapEl(childId);
            }
        }
    }
    recurseHeapEl(id) {
        if (this.seen.has(id)) {
            return; // already recursed
        }
        this.seen.add(id);
        const heapEl = this.heap[id];
        if (heapEl[0] == 'LIST' || heapEl[0] == 'TUPLE' || heapEl[0] == 'SET') {
            heapEl.slice(1).forEach(el => this.recurseSubEl(el, id));
        }
        else if (heapEl[0] == 'DICT') {
            heapEl.slice(1).forEach(el => {
                const key = el[0];
                const val = el[1];
                if (isReference(key)) {
                    const childId = key[1];
                    if (!shouldNest(this.heap[childId])) {
                        this.recurseHeapEl(childId);
                    }
                }
                this.recurseSubEl(val, id);
            });
        }
        else if (heapEl[0] == 'INSTANCE' || heapEl[0] == 'INSTANCE_PPRINT' || heapEl[0] == 'CLASS') {
            const headerLength = heapEl[0] == 'INSTANCE' ? 2 : 3;
            heapEl.slice(headerLength).forEach(el => this.recurseSubEl(el[1], id));
        }
        else if (heapEl[0] == 'FUNCTION' && heapEl.length == 4) {
            heapEl[3].forEach(el => this.recurseSubEl(el[1], id));
        }
        if (!this.nodeById.has(id)) {
            this.insertNode(id);
        }
    }
}
// always nest values of these types within objects to make the
// visualization look cleaner:
// - functions should be nested within heap objects since that's a more
//   intuitive rendering for methods (i.e., functions within objects)
// - lots of special cases for function/property-like python types that we should inline
const NESTED_TYPES = new Set(['FUNCTION',
    'property', 'classmethod', 'staticmethod', 'builtin_function_or_method',
    'member_descriptor', 'getset_descriptor', 'method_descriptor', 'wrapper_descriptor']);
function shouldNest(heapEl) {
    if (NESTED_TYPES.has(heapEl[0])) {
        return true;
    }
    else if (heapEl[0] === 'INSTANCE' || heapEl[0] === 'INSTANCE_PPRINT') {
        // Specific instances that we want to nest
        return NESTED_TYPES.has(heapEl[1]);
    }
    return false;
}
// Returns true for: lists and tuples of the same length
// Dicts, instances, and instance_pprints with the same fields
function isStructurallyEquivalent(a, b) {
    if (a[0] !== b[0]) {
        return false;
    }
    if (a.length !== b.length) {
        return false;
    }
    if (a[0] === "LIST" || a[0] === "TUPLE") {
        return true;
    }
    let startingInd = -1;
    if (a[0] == 'DICT') {
        startingInd = 1;
    }
    else if (a[0] == 'INSTANCE') {
        startingInd = 2;
    }
    else if (a[0] == 'INSTANCE_PPRINT') {
        startingInd = 3;
    }
    else {
        return false; // we don't care about all other types
    }
    // build a set of all the fields in a
    const fields = new Set();
    for (let i = startingInd; i < a.length; i++) {
        fields.add(JSON.stringify(a[i][0]));
    }
    // check that all fields in b are in a
    for (let i = startingInd; i < b.length; i++) {
        if (!fields.has(JSON.stringify(b[i][0]))) { // eslint-disable-line @typescript-eslint/no-explicit-any
            return false;
        }
    }
    return true;
}
function isReference(el) {
    return el instanceof Array && el[0] === "REF";
}
//# sourceMappingURL=HeapLayout.js.map